import * as React from "react";
declare const I18n;
import { Collapse } from "react-collapse";
import DOMPurify from "dompurify/dist/purify.min";

import { Lesson } from "../../../../types";

import Filter from "bad-words";
import Header from "../../../Header";
import BreadCrumb from "../../../BreadCrumb";
import Section from "../../../Section";
import Question from "../../../Question";
import Metadata from "../../../Metadata/Metadata";
import ProgressBar from "../../../ProgressBar/ProgressBar";
import Sidebar from "../../components/Sidebar";
import CompletedMetric from "../../components/CompletedMetric"
import Achievements from "../../components/Achievements";
import ShowQuiz from "./quiz";
import WomenInStemModal from "../../../Achievements/modals/WomenInStemModal"
import getSkillsIcon from '../../../../utils/getSkillsIcon';
import getCourseDifficultyLabel from '../../../../utils/getCourseDifficultyLabel';

import api from "../../../../api";

type Props = {
  initialData: {
    lesson: Lesson;
    courseId: number;
    isFacilitator: boolean;
    lessonCompleted: boolean;
    renderFromLessonIndex: boolean;
    allLessons: Lesson[];
    courseLevel?: number;
    miniLesson?: boolean;
    lessonsProgress?: any;
    facilitatorCourseProgress?: any;
  },
  sectionIndex: number;
  onNextLesson: () => void;
  onPreviousLesson: () => void;
  onLessonClick: (lesson: Lesson, sectionIndex: number) => void;
  quizQuestionsByLesson?: any;
  showAllSections?: boolean;
  totalDuration?: number;
  totalLessons?: number;
  completedLessons?: number;
  learnersCourseCompleted: number;
  totalLearners: number;
};

const ShowInteractiveLesson: React.FunctionComponent<Props> = ({
  initialData: { lesson, courseId, courseLevel, isFacilitator, lessonCompleted = false, renderFromLessonIndex = false, allLessons, miniLesson, lessonsProgress: initialLessonsProgress, facilitatorCourseProgress },
  sectionIndex = 0,
  onNextLesson,
  onPreviousLesson,
  onLessonClick,
  quizQuestionsByLesson,
  showAllSections = false,
  totalLessons,
  totalDuration,
  completedLessons,
  learnersCourseCompleted,
  totalLearners

}: Props): JSX.Element => {
  const [lessonsProgress, setLessonsProgress] = React.useState(initialLessonsProgress);
  const [sectionsArray, setSectionsArray] = React.useState([]);
  const [sectionsAccordion, setSectionsAccordion] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [badWordsError, setBadWordsError] = React.useState(false);
  const [sectionMissingInputError, setSectionMissingInputError] = React.useState(false)
  const [canEdit, setCanEdit] = React.useState(false);
  const [filter] = React.useState(new Filter());
  const [currentSection, setCurrentSection] = React.useState(0);
  const [newBadges, setNewBadges] = React.useState([]);
  const [modalIndex, setModalIndex] = React.useState(0);
  const [showBadgeModal, setShowBadgeModal] = React.useState(false);
  const lastSectionIndex = lesson.sections.length
  const lessonId = lesson.id
  const DOMPurifyConfig = {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["target", "allow", "allowfullscreen", "frameborder", "scrolling", "src", "width", "height"],
  };
  const isQuizLesson = lesson.lesson_type === "quiz"
  filter.removeWords("hui");

  React.useEffect(() => {
    const sectionArray = lesson.sections.map((section) => {
      const questions = section.questions.map((question) => {
        const hasValue = question.submission.length > 0;
        return {
          value: hasValue ? question.submission[0].answer : "",
          error: "",
          id: hasValue ? question.submission[0].id : "",
        };
      });

      return {
        isOpen: false,
        questions,
      };
    });

    setSectionsArray(sectionArray);

    const accordionArray = lesson.sections.map((section) => ({
      id: section.id,
      isOpen: false,
    }));
    setSectionsAccordion(accordionArray);
  }, [lesson, currentSection]);

  React.useEffect(() => {
    setCurrentSection(0)
  }, [lesson]);

  React.useEffect(() => {
    setCurrentSection(sectionIndex)
  }, [lesson, sectionIndex]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [lesson.id]);

  React.useEffect(() => {
    if (lessonCompleted) {
      setCanEdit(false)
    } else {
      setCanEdit(true)
    }
  }, [lessonCompleted])


  React.useEffect(() => {
    if (currentSection > 0 && !showAllSections) {
      let activeSection = sectionsArray[currentSection - 1];

      setSectionMissingInputError(false);

      if (activeSection?.questions) {
        const hasMissingInput = activeSection.questions.some((q) => {
          const v = q.value;

          // File upload questions
          if (v instanceof File) return false;
          if (Array.isArray(v) && v[0] instanceof File) return false;

          // Missing text
          if (v === undefined || v === null) return true;

          // Text input
          if (typeof v === "string") return v.trim().length < 1;

          // Anything else = missing
          return true;
        });

        if (hasMissingInput) setSectionMissingInputError(true);
      }
    }
  }, [currentSection, sectionsArray, showAllSections]);

  const handleSubmit = () => {
    let error = false;
    const formData = new FormData();

    formData.append("course_level", courseLevel.toString());

    if (error) {
      document.getElementById("section_1")?.scrollIntoView();
      setLoading(false);
      return Promise.resolve(null);
    }

    return api.submitLesson(courseId, lesson.id, formData)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        setLoading(false);
        throw error;
      });
  };

  const handleSubmitFacilitator = () => {
    const formData = new FormData();

    formData.append("course_level", courseLevel.toString());

    return api.submitLessonFacilitator(courseId, lesson.id, formData)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        setLoading(false);
        throw error;
      });
  }

  const handleCompleteLesson = async () => {
    if (isFacilitator) return Promise.resolve(true);

    await handleSubmitProgress();
    const response = await handleSubmit();

    const badges = response?.newBadges || [];

    if (badges.length > 0) {
      setNewBadges(badges);
      setModalIndex(0);
      setShowBadgeModal(true);
      return;
    }

    // No badges → continue normally
    handleNextLesson();
  };

  const handleCompleteLessonFacilitator = async () => {

    await handleSubmitProgress();
    const response = await handleSubmitFacilitator();

    handleNextLesson();
  };

  const handleCloseBadgeModal = () => {
    if (newBadges.length === 1) {
      setShowBadgeModal(false);
      handleNextLesson();
      return;
    }

    const isLastItem = modalIndex === newBadges.length - 1;

    if (isLastItem) {
      // Close ALL modals
      setShowBadgeModal(false);
      setModalIndex(0);
      handleNextLesson();
      return;
    }

    const updatedBadges = [...newBadges];
    updatedBadges.splice(modalIndex, 1);

    setNewBadges(updatedBadges);

    setModalIndex(0);
  };

  const handleNextBadge = () => {
    setModalIndex((prev) => (prev + 1) % newBadges.length);
  };

  const handlePrevBadge = () => {
    setModalIndex((prev) =>
      prev === 0 ? newBadges.length - 1 : prev - 1
    );
  };

  const handleSubmitProgress = async () => {
    const formData = new FormData();

    formData.append("lessonId", lessonId);

    if (showAllSections) {
      // When showing all sections, submit all of them
      formData.append("sectionId", "all");

      lesson.sections.forEach((rawSection, sectionIdx) => {
        const sectionWithAnswers = sectionsArray[sectionIdx];

        if (sectionWithAnswers && sectionWithAnswers.questions) {
          sectionWithAnswers.questions.forEach((answer, questionIdx) => {
            const question = rawSection.questions[questionIdx];
            if (!question) return;

            const value = answer.value;
            const submissionIndex = sectionIdx * 100 + questionIdx;

            formData.append(`submissions[${submissionIndex}][submittable_type]`, "Question");
            formData.append(`submissions[${submissionIndex}][submittable_id]`, question.id);

            const isFile = value instanceof File;
            const isValidString = typeof value === "string" && value.trim().length > 0;

            if (isFile) {
              formData.append(`submissions[${submissionIndex}][file]`, value);
            } else if (isValidString) {
              formData.append(`submissions[${submissionIndex}][answer]`, value);
            }

            if (answer.id) {
              formData.append(`submissions[${submissionIndex}][id]`, answer.id);
            }
          });
        }
      });
    } else if (currentSection === 0) {
      formData.append("sectionId", "overview");
    } else {
      const rawSection = lesson.sections[currentSection - 1];
      const sectionWithAnswers = sectionsArray[currentSection - 1];

      formData.append("sectionId", rawSection.id);

      sectionWithAnswers.questions.forEach((answer, index) => {
        const question = rawSection.questions[index];
        const value = answer.value;

        formData.append(`submissions[${index}][submittable_type]`, "Question");
        formData.append(`submissions[${index}][submittable_id]`, question.id);

        const isFile = value instanceof File;
        const isValidString = typeof value === "string" && value.trim().length > 0;

        if (isFile) {
          formData.append(`submissions[${index}][file]`, value);
        } else if (isValidString) {
          formData.append(`submissions[${index}][answer]`, value);
        }

        if (answer.id) {
          formData.append(`submissions[${index}][id]`, answer.id);
        }
      });
    }

    try {
      // Submit the progress
      await api.updateCourseLessonsProgress(courseId, formData);

      // Fetch the updated progress
      const progressData = await api.getCourseLessonsProgress(courseId);

      // Update state with the fresh data
      if (progressData && progressData.lessons_progress) {
        setLessonsProgress(progressData.lessons_progress);
      } else if (progressData) {
        setLessonsProgress(progressData);
      }

      return progressData;
    } catch (error) {
      console.error("Error updating progress:", error);
      throw error;
    }
  };

  const goToPrevSection = () => {
    if (currentSection > 0) {
      setCurrentSection((currentSection) => currentSection - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextSection = () => {
    if (currentSection < lesson.sections.length) {
      handleSubmitProgress()
      setCurrentSection((currentSection) => currentSection + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  let breadCrumbLocations = [
    {
      title: I18n.t("models.course.attributes.courses"),
      url: "/courses",
    },
  ]

  if (!renderFromLessonIndex) {
    breadCrumbLocations.push({ title: lesson.course_title, url: `/courses/${courseId}/lessons` },)
    breadCrumbLocations.push({ title: lesson.title, url: "" },)
  } else {
    breadCrumbLocations.push({ title: lesson.course_title, url: "" },)

  }

  const handleLessonClick = (selectedLesson: Lesson, selectedSectionIndex: number) => {
    if (selectedLesson.id === lesson.id) {
      setCurrentSection(selectedSectionIndex);

      if (!showAllSections) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (selectedSectionIndex > 0) {
        const sectionElement = document.querySelector(`[data-section-id="${selectedLesson.sections[selectedSectionIndex - 1]?.id}"]`);
        if (sectionElement) {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        const overviewElement = document.querySelector('[data-section-id="overview"]');
        if (overviewElement) {
          overviewElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else {
      onLessonClick(selectedLesson, selectedSectionIndex);
    }
  };

  const handleNextLesson = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    onNextLesson();
  };

  const handlePreviousLesson = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    onPreviousLesson();
  };

  return (
    <div className="lessons_show">

      <BreadCrumb
        locations={breadCrumbLocations}
      />

      {!isQuizLesson && (
        <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column' }}>
          <Header title={lesson.title} showIcon={false} />
          <div className="lesson_progress">
            {!showAllSections && <ProgressBar value={completedLessons} total={totalLessons} displayStep={false} />}
          </div>
        </div>
      )}

      <div className="lessons_show__column">
        {
          isQuizLesson ? (
            <div className="lessons_show__body">
              {currentSection === 0 && (
                <div className="lessons_show__section" style={{ marginBottom: "0px" }}>
                  <Section title={lesson.title} index={0} />

                  <div
                    className="section__body app-text"
                    style={{ paddingLeft: "0px" }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(lesson.overview, DOMPurifyConfig),
                    }}
                  />
                </div>
              )}
              <ShowQuiz quizQuestions={quizQuestionsByLesson} courseId={courseId} lessonId={lesson.id} onNextLesson={onNextLesson} onPreviousLesson={onPreviousLesson} isFacilitator={isFacilitator} />
            </div>
          ) : (
            <div className="lessons_show__body">
              {/* Overview Section */}
              {(currentSection === 0 || showAllSections) && (
                <div className={showAllSections ? "lessons_show__overview" : "lessons_show__body"}>
                  <div className="lessons_show__title">
                    <Section title={`${1}. ${I18n.t("views.common.overview")}`} index={0} />
                    {showAllSections &&
                      <Metadata
                        items={[
                          totalLessons > 0 && {
                            icon: <i className="fa-regular fa-book" />,
                            title: `${totalLessons} ${I18n.t("models.course.type.lesson")}${totalLessons !== 1 ? 's' : ''}`
                          },
                          totalDuration > 0 && {
                            icon: <i className="fa-regular fa-clock" />,
                            title: `${totalDuration} ${I18n.t("models.course.type.lite_bytes")}`
                          },
                          {
                            icon: <i className="fa fa-signal" />,
                            title: getCourseDifficultyLabel(courseLevel)
                          }
                        ].filter(Boolean)}
                      />
                    }
                  </div>
                  <div
                    className="section__body app-text"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(lesson.overview, DOMPurifyConfig),
                    }}
                  />

                  {!showAllSections && lesson.skills && lesson.skills.length > 0 && (
                    <>
                      <Section title={I18n.t("models.skills.messaging.frc")} />
                      <div className="frc__skills">
                        {lesson.skills.map((skill) => (
                          <div key={skill.id} className="frc__skill_info">
                            {getSkillsIcon(skill.slug)}
                            <div className="frc_label">
                              <h4>{I18n.t(`models.skills.attributes.${skill.slug}`)}</h4>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                </div>
              )}

              {/* All Sections */}
              {
                lesson.sections.map((section, index) => {
                  const actualIndex = index + 1;
                  if (!showAllSections && currentSection !== actualIndex) return null;

                  return (
                    <div className={showAllSections ? "lessons_show__section_first_lesson" : "lessons_show__section"} key={section.id}>
                      <Section
                        title={`${actualIndex + 1}. ${section.title}`}
                        withInput={section.questions.length > 0}
                        withBorder={showAllSections}
                        index={index + 1}
                        iconId={section.id}
                        onArrowPress={showAllSections ? () => {
                          const newArray = [...sectionsAccordion];
                          newArray[index].isOpen = !newArray[index].isOpen;
                          setSectionsAccordion(newArray);
                        } : undefined}
                      />
                      {showAllSections ? (
                        <Collapse
                          isOpened={sectionsAccordion[index] && sectionsAccordion[index].isOpen}
                        >
                          <div
                            className="section__body app-text"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                section.content,
                                DOMPurifyConfig
                              ),
                            }}
                          />

                          <div className="section_question__body">
                            {section.questions.map((question, ind) => {
                              if (sectionsArray[index] && sectionsArray[index].questions[ind]) {
                              }

                              if (!sectionsArray[index] || !sectionsArray[index].questions || !sectionsArray[index].questions[ind]) {
                                return null;
                              }

                              return (
                                <div key={`question-${question.id || ind}`} >
                                  <Question
                                    key={`${index}_${ind}`}
                                    title={question.title}
                                    value={sectionsArray[index].questions[ind].value}
                                    error={sectionsArray[index].questions[ind].error}
                                    inputId={`question_input_${question.id}`}
                                    inputType={question.input_type}
                                    onBlur={(value) => {
                                      if (filter.isProfane(value)) {
                                        const newArray = sectionsArray;

                                        newArray[index].questions[ind].error = I18n.t(
                                          "models.lesson.messaging.bad_language"
                                        );

                                        setSectionsArray([...newArray]);
                                        setBadWordsError(true);
                                      }
                                    }}
                                    isEdit={canEdit}
                                    isFacilitator={isFacilitator}
                                    onChange={(value) => {
                                      const newArray = sectionsArray;

                                      newArray[index].questions[ind].value = value;
                                      newArray[index].questions[ind].error = "";
                                      setSectionsArray([...newArray]);
                                      setBadWordsError(false);
                                    }}
                                  />
                                  {true && (
                                    <small style={{ color: '#42C0C6' }}>
                                      <i className="fa-light fa-triangle-exclamation me-2"></i>
                                      {I18n.t("models.lesson.messaging.no_edit")}
                                    </small>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </Collapse>
                      ) : (
                        <>
                          <div
                            className="section__body app-text"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                section.content,
                                DOMPurifyConfig
                              ),
                            }}
                          />

                          <div className="section_question__body">
                            {section.questions.map((question, ind) => {
                              if (sectionsArray[index] && sectionsArray[index].questions[ind]) {
                              }

                              if (!sectionsArray[index] || !sectionsArray[index].questions || !sectionsArray[index].questions[ind]) {
                                return null;
                              }

                              return (
                                <div key={`question-${question.id || ind}`} >
                                  <Question
                                    key={`${index}_${ind}`}
                                    title={question.title}
                                    value={sectionsArray[index].questions[ind].value}
                                    error={sectionsArray[index].questions[ind].error}
                                    inputId={`question_input_${question.id}`}
                                    inputType={question.input_type}
                                    onBlur={(value) => {
                                      if (filter.isProfane(value)) {
                                        const newArray = sectionsArray;

                                        newArray[index].questions[ind].error = I18n.t(
                                          "models.lesson.messaging.bad_language"
                                        );

                                        setSectionsArray([...newArray]);
                                        setBadWordsError(true);
                                      }
                                    }}
                                    isEdit={canEdit}
                                    isFacilitator={isFacilitator}
                                    onChange={(value) => {
                                      const newArray = sectionsArray;

                                      newArray[index].questions[ind].value = value;
                                      newArray[index].questions[ind].error = "";
                                      setSectionsArray([...newArray]);
                                      setBadWordsError(false);
                                    }}
                                  />
                                  {true && (
                                    <small style={{ color: '#42C0C6' }}>
                                        <i className="fa-light fa-triangle-exclamation  me-2"></i>
                                      {I18n.t("models.lesson.messaging.no_edit")}
                                    </small>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}

                    </div>
                  );
                })
              }

              {/* Achievements */}
              {
                showAllSections && (
                  <Achievements lessons={allLessons} />
                )
              }

              {/* Navigation Buttons */}
              {
                !showAllSections && (
                  <div className="d-flex mt-3">
                    {currentSection === 0 && (
                      <button
                        className="btn next_previous_buttons"
                        onClick={handlePreviousLesson}
                      >
                        <i className="fa-solid fa-angle-left"></i>
                        {I18n.t("views.common.prev_lesson")}
                      </button>
                    )}
                    {currentSection > 0 && (
                      <button
                        className="btn next_previous_buttons"
                        onClick={goToPrevSection}
                      >
                        <i className="fa-solid fa-angle-left"></i>
                        {I18n.t("views.common.prev")}
                      </button>
                    )}
                    {
                      currentSection < lastSectionIndex && (
                        <button
                          className="btn next_previous_buttons ms-auto"
                          onClick={goToNextSection}
                          disabled={!isFacilitator && (loading || badWordsError || sectionMissingInputError)}
                        >
                          {I18n.t("views.common.next")}
                          <i className="fa-solid fa-angle-right"></i>
                        </button>
                      )
                    }
                    {
                      currentSection === lastSectionIndex && (
                        <>
                          {isFacilitator ? (
                            <button disabled={loading} onClick={handleCompleteLessonFacilitator} className="btn next_previous_buttons  ms-auto" >
                              {I18n.t("models.lesson.messaging.show")}
                              {loading && (
                                <i className="far fa-spinner-third fa-spin btn-loader ml-2"></i>
                              )}
                              {!loading && (
                                <i className="fa-solid fa-angle-right"></i>
                              )}
                            </button>
                          ) : (
                            <button
                              type="submit"
                              name="commit"
                              disabled={!isFacilitator && (loading || badWordsError || sectionMissingInputError)}
                              className="btn next_previous_buttons  ms-auto"
                              onClick={handleCompleteLesson}
                            >
                              {I18n.t("models.lesson.messaging.show")}
                              {loading && (
                                <i className="far fa-spinner-third fa-spin btn-loader ml-2"></i>
                              )}
                              {!loading && (
                                <i className="fa-solid fa-angle-right"></i>
                              )}
                            </button>
                          )}</>

                      )
                    }
                  </div>
                )
              }

              {/* Complete Button */}
              {
                showAllSections && (
                  <div className="d-flex mt-3">
                    {isFacilitator ? (
                      <button disabled={loading} onClick={handleNextLesson} className="btn next_previous_buttons  ms-auto" >
                        {I18n.t("views.common.next")}
                        {loading && (
                          <i className="far fa-spinner-third fa-spin btn-loader ml-2"></i>
                        )}
                        {!loading && (
                          <i className="fa-solid fa-angle-right"></i>
                        )}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        name="commit"
                        disabled={!isFacilitator && (loading || badWordsError)}
                        className="btn next_previous_buttons  ms-auto"
                        onClick={handleCompleteLesson}
                      >
                        {I18n.t("views.common.next")}
                        {loading && (
                          <i className="far fa-spinner-third fa-spin btn-loader ml-2"></i>
                        )}
                        {!loading && (
                          <i className="fa-solid fa-angle-right"></i>
                        )}
                      </button>
                    )}
                  </div>
                )
              }
            </div >
          )
        }
        <div>
          {isFacilitator && (
            <CompletedMetric courseId={String(courseId)} learnersCourseCompleted={learnersCourseCompleted} totalLearners={totalLearners} />
          )}
          <Sidebar
            lessons={allLessons}
            currentLesson={lesson}
            currentSection={currentSection}
            onLessonClick={handleLessonClick}
            showAllSections={showAllSections}
            isMiniLesson={miniLesson || false}
            lessonsProgress={lessonsProgress}
            facilitatorCourseProgress={facilitatorCourseProgress}
            isFacilitator={isFacilitator}
          />
          <div />
          {
            newBadges.length > 0 &&
            modalIndex < newBadges.length && (
              <WomenInStemModal
                name={newBadges[modalIndex].name}
                skillSlug={newBadges[modalIndex].skillSlug}
                info={newBadges[modalIndex].description}
                pointsRequired={newBadges[modalIndex].pointsRequired}
                imageUrl={newBadges[modalIndex].file_url}
                isNew={true}
                show={showBadgeModal}
                closeModal={handleCloseBadgeModal}
                hasMultiple={newBadges.length > 1}
                onNext={handleNextBadge}
                onPrev={handlePrevBadge}
                disablePrev={modalIndex === 0}
                disableNext={modalIndex === newBadges.length - 1}
              />
            )}
        </div>
      </div >
    </div >
  );
};

export default ShowInteractiveLesson;